/**
 * src/lib/api/resumeUploadsOnStart.js — TICKET-MINIO-RESUMABLE Slice 3b.
 *
 * App-start drain for interrupted resumable uploads. iOS Safari can't upload in
 * the background (no Background Fetch API), so the model is resume-on-reopen: on
 * app load, re-PUT the missing parts of any persisted multipart upload and finish
 * it. Flag-gated (VITE_MINIO_RESUMABLE) — a no-op when the resumable path is off,
 * so it adds nothing to a normal app start.
 *
 * Wired (fire-and-forget) from AppShell's uploadManager.start() effect.
 */
import { resumeIncompleteUploads } from './resumableUploadStore';
import { postRegisterAndProcess } from './voice-v2';
import { apiFetch } from './client';

const MINIO_RESUMABLE_ENABLED = (() => {
  try { const v = import.meta.env?.VITE_MINIO_RESUMABLE; return v === '1' || v === 'true'; }
  catch { return false; }
})();

/**
 * Build the register body for a resumed upload and register+process it. Mirrors
 * uploadVoiceFile's registerBody shape — a genuinely different entry point
 * (post-reload, rebuilt from the persisted record rather than a live File) that
 * shares the register/process POST mechanics via postRegisterAndProcess.
 */
function finalizeResumed(fetchImpl) {
  return async ({ objectKey, storageBackend, scopeType, scopeId, fileName, fileSize, mimeType }) => {
    const registerBody = {
      action: 'voice.register_upload',
      scope_type: scopeType,
      scope_id: scopeId,
      file_path: objectKey,
      file_name: fileName,
      file_size_bytes: fileSize,
      mime_type: mimeType,
      context_type: scopeType,
      storage_backend: storageBackend || 'hetzner',
    };
    if (scopeType === 'project') registerBody.project_id = scopeId;
    await postRegisterAndProcess({ registerBody, fetchImpl, obsBase: { scope_type: scopeType, scope_id: scopeId } });
  };
}

/**
 * Resume any interrupted multipart uploads. Call once at app start (fire-and-forget).
 * `enabled` / `apiFetchImpl` / `xhrImpl` are injectable for tests; prod uses the flag
 * + real apiFetch + window.XMLHttpRequest.
 * @returns {Promise<{resumed:number, completed:number, failed:number, skipped?:boolean}>}
 */
export async function resumeIncompleteUploadsOnStart({
  apiFetchImpl = apiFetch,
  xhrImpl,
  enabled = MINIO_RESUMABLE_ENABLED,
} = {}) {
  if (!enabled) return { resumed: 0, completed: 0, failed: 0, skipped: true };
  return resumeIncompleteUploads({ apiFetchImpl, xhrImpl, finalize: finalizeResumed(apiFetchImpl) });
}
